import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import {
  Camera, User, Mail, Phone, Shield, Lock,
  Edit3, Save, X, Eye, EyeOff, CheckCircle2,
  AlertCircle, Trash2, Calendar, FileText, CreditCard,
  Upload, Download, File,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, PageHeader, UserAvatar, FileViewerModal,
} from "../ui";
import "./account-settings.scss";

/* ---------- helpers ---------- */
const roleLabel = (t) =>
  t === "tenantadmin" ? "Tenant Administrator" : t === "superadmin" ? "Super Administrator" : t === "admin" ? "Administrator" : t === "instructor" ? "Instructor" : t === "student" ? "Student" : t || "User";

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric", timeZone: "Asia/Kolkata" });
};

/* ========== component ========== */
const AccountSettingsPage = () => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const userId = storedUser?._id;

  /* --- state --- */
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editProfile, setEditProfile] = useState(false);
  const [editPassword, setEditPassword] = useState(false);

  // Profile fields
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: "", text: "" });

  // Password fields
  const [pw, setPw] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: "", text: "" });
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Photo upload
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileRef = useRef();

  // Government ID upload
  const [govtIdUploading, setGovtIdUploading] = useState(false);
  const [govtIdPreview, setGovtIdPreview] = useState(null);
  const [govtIdKey, setGovtIdKey] = useState("");
  const govtIdRef = useRef();

  // Student resume
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeKey, setResumeKey] = useState("");
  const [resumeUrl, setResumeUrl] = useState(null);
  const resumeRef = useRef();
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeModalUrl, setResumeModalUrl] = useState("");

  /* --- fetch user --- */
  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/users/${userId}`)
      .then(async ({ data }) => {
        setUser(data.user);
        setProfile({ name: data.user.name, phone: data.user.phone || "" });

        // Load profile photo presigned URL
        if (data.user.profilePhoto) {
          apiClient
            .get(`/files/presign-get?key=${encodeURIComponent(data.user.profilePhoto)}`)
            .then(({ data: d }) => setPhotoPreview(d.url))
            .catch(() => {});
        }

        // Load government ID card presigned URL
        if (data.user.govtIdCard) {
          setGovtIdKey(data.user.govtIdCard);
          apiClient
            .get(`/files/presign-get?key=${encodeURIComponent(data.user.govtIdCard)}`)
            .then(({ data: d }) => setGovtIdPreview(d.url))
            .catch(() => {});
        }

        // Load student resume key (from Student model via a separate call)
        if (data.user.usertype === "student" && data.user.studentId) {
          try {
            const { data: sData } = await apiClient.get(`/students/${data.user.studentId}`);
            const student = sData.student || sData;
            if (student.resume) {
              setResumeKey(student.resume);
              apiClient
                .get(`/files/presign-get?key=${encodeURIComponent(student.resume)}`)
                .then(({ data: d }) => setResumeUrl(d.url))
                .catch(() => {});
            }
          } catch {}
        }
      })
      .catch(() => setProfileMsg({ type: "error", text: "Failed to load profile." }))
      .finally(() => setLoading(false));
  }, [userId]);

  /* --- profile photo upload via server-side upload --- */
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setProfileMsg({ type: "error", text: "Please select an image file." });
      return;
    }
    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setProfileMsg({ type: "error", text: "Image must be less than 5 MB." });
      return;
    }

    setPhotoUploading(true);
    setProfileMsg({ type: "", text: "" });

    try {
      // 1. Upload via backend
      const formData = new FormData();
      formData.append("file", file);
      const { data: uploadData } = await apiClient.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const s3Key = uploadData.key;

      // 2. Save the key to user model
      const { data: updateData } = await apiClient.put(`/users/${userId}/profile-photo`, {
        profilePhoto: s3Key,
      });
      setUser(updateData.user);

      // 3. Get a presigned read URL for preview
      const { data: readData } = await apiClient.get(
        `/files/presign-get?key=${encodeURIComponent(s3Key)}`
      );
      setPhotoPreview(readData.url);
      setProfileMsg({ type: "success", text: "Profile photo updated!" });
    } catch {
      setProfileMsg({ type: "error", text: "Failed to upload photo." });
    }
    setPhotoUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePhotoRemove = async () => {
    setPhotoUploading(true);
    try {
      // Delete from S3 if key exists
      if (user.profilePhoto) {
        await apiClient.delete("/files/delete", { data: { key: user.profilePhoto } });
      }
      await apiClient.put(`/users/${userId}/profile-photo`, { profilePhoto: "" });
      setPhotoPreview(null);
      setUser((u) => ({ ...u, profilePhoto: "" }));
      setProfileMsg({ type: "success", text: "Profile photo removed." });
    } catch {
      setProfileMsg({ type: "error", text: "Failed to remove photo." });
    }
    setPhotoUploading(false);
  };

  /* --- government ID card upload --- */
  const handleGovtIdSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      setProfileMsg({ type: "error", text: "Please select an image or PDF file for your Government ID." });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setProfileMsg({ type: "error", text: "Government ID file must be less than 10 MB." });
      return;
    }
    setGovtIdUploading(true);
    setProfileMsg({ type: "", text: "" });
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: uploadData } = await apiClient.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const s3Key = uploadData.key;
      const { data: updateData } = await apiClient.put(`/users/${userId}/govt-id`, { govtIdCard: s3Key });
      setUser(updateData.user);
      setGovtIdKey(s3Key);
      if (file.type.startsWith("image/")) {
        const { data: readData } = await apiClient.get(`/files/presign-get?key=${encodeURIComponent(s3Key)}`);
        setGovtIdPreview(readData.url);
      } else {
        setGovtIdPreview(null); // PDF — no image preview
      }
      setProfileMsg({ type: "success", text: "Government ID uploaded!" });
    } catch {
      setProfileMsg({ type: "error", text: "Failed to upload Government ID." });
    }
    setGovtIdUploading(false);
    if (govtIdRef.current) govtIdRef.current.value = "";
  };

  const handleGovtIdRemove = async () => {
    setGovtIdUploading(true);
    try {
      if (govtIdKey) await apiClient.delete("/files/delete", { data: { key: govtIdKey } });
      await apiClient.put(`/users/${userId}/govt-id`, { govtIdCard: "" });
      setGovtIdPreview(null);
      setGovtIdKey("");
      setUser((u) => ({ ...u, govtIdCard: "" }));
      setProfileMsg({ type: "success", text: "Government ID removed." });
    } catch {
      setProfileMsg({ type: "error", text: "Failed to remove Government ID." });
    }
    setGovtIdUploading(false);
  };

  const handleGovtIdView = async () => {
    if (!govtIdKey) return;
    try {
      const { data } = await apiClient.get(`/files/presign-get?key=${encodeURIComponent(govtIdKey)}`);
      window.open(data.url, "_blank");
    } catch {
      setProfileMsg({ type: "error", text: "Failed to load document." });
    }
  };

  /* --- resume upload (students only) --- */
  const handleResumeSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowed.includes(file.type)) {
      setProfileMsg({ type: "error", text: "Please upload a PDF or DOCX file for your resume." });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setProfileMsg({ type: "error", text: "Resume must be less than 10 MB." });
      return;
    }
    setResumeUploading(true);
    setProfileMsg({ type: "", text: "" });
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: uploadData } = await apiClient.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const s3Key = uploadData.key;
      await apiClient.put(`/users/${userId}/resume`, { resume: s3Key });
      setResumeKey(s3Key);
      const { data: readData } = await apiClient.get(`/files/presign-get?key=${encodeURIComponent(s3Key)}`);
      setResumeUrl(readData.url);
      setProfileMsg({ type: "success", text: "Resume uploaded!" });
    } catch {
      setProfileMsg({ type: "error", text: "Failed to upload resume." });
    }
    setResumeUploading(false);
    if (resumeRef.current) resumeRef.current.value = "";
  };

  const handleResumeRemove = async () => {
    setResumeUploading(true);
    try {
      if (resumeKey) await apiClient.delete("/files/delete", { data: { key: resumeKey } });
      await apiClient.put(`/users/${userId}/resume`, { resume: "" });
      setResumeUrl(null);
      setResumeKey("");
      setProfileMsg({ type: "success", text: "Resume removed." });
    } catch {
      setProfileMsg({ type: "error", text: "Failed to remove resume." });
    }
    setResumeUploading(false);
  };

  const handleResumeView = async () => {
    if (!resumeKey) return;
    try {
      const { data } = await apiClient.get(`/files/presign-get?key=${encodeURIComponent(resumeKey)}`);
      setResumeModalUrl(data.url);
      setShowResumeModal(true);
    } catch {
      setProfileMsg({ type: "error", text: "Failed to load resume." });
    }
  };

  /* --- update profile --- */
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileMsg({ type: "", text: "" });
    setProfileSaving(true);
    try {
      const { data } = await apiClient.put(`/users/${userId}`, {
        name: profile.name,
        phone: profile.phone,
      });
      setUser(data.user);
      setProfileMsg({ type: "success", text: "Profile updated successfully." });
      setEditProfile(false);
    } catch {
      setProfileMsg({ type: "error", text: "Failed to update profile." });
    }
    setProfileSaving(false);
  };

  /* --- update password --- */
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwMsg({ type: "", text: "" });
    if (!pw.oldPassword || !pw.newPassword || !pw.confirmPassword) {
      setPwMsg({ type: "error", text: "Please fill in all fields." });
      return;
    }
    if (pw.newPassword.length < 6) {
      setPwMsg({ type: "error", text: "New password must be at least 6 characters." });
      return;
    }
    if (pw.newPassword !== pw.confirmPassword) {
      setPwMsg({ type: "error", text: "New passwords do not match." });
      return;
    }
    setPwSaving(true);
    try {
      await apiClient.put(`/users/${userId}/update-password`, {
        oldPassword: pw.oldPassword,
        newPassword: pw.newPassword,
      });
      setPwMsg({ type: "success", text: "Password changed successfully." });
      setPw({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setEditPassword(false);
    } catch {
      setPwMsg({ type: "error", text: "Failed to update password. Check your current password." });
    }
    setPwSaving(false);
  };

  /* --- loading state --- */
  if (loading) {
    return (
      <div className="acct-page">
        <div className="acct-page__loading">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="acct-page">
        <div className="acct-page__loading">
          <AlertCircle size={32} />
          <p>Failed to load user profile.</p>
        </div>
      </div>
    );
  }

  /* ========== render ========== */
  return (
    <div className="acct-page">

      {/* ---- Profile Hero Card ---- */}
      <AppCard className="acct-hero">
        <AppCardContent>
          <div className="acct-hero__inner">
            {/* Avatar with camera overlay */}
            <div className="acct-hero__avatar-wrapper">
              <UserAvatar name={user.name} src={photoPreview || undefined} size="xl" bordered />
              <button
                className="acct-hero__camera-btn"
                onClick={() => fileRef.current?.click()}
                disabled={photoUploading}
                title="Change profile photo"
              >
                {photoUploading ? <Spinner size="sm" animation="border" /> : <Camera size={16} />}
              </button>
              <input
                type="file"
                ref={fileRef}
                accept="image/*"
                style={{ display: "none" }}
                onChange={handlePhotoSelect}
              />
            </div>

            {/* Name + role */}
            <div className="acct-hero__info">
              <h2 className="acct-hero__name">{user.name}</h2>
              <span className="acct-hero__role">{roleLabel(user.usertype)}</span>
              <span className="acct-hero__email">
                <Mail size={14} /> {user.email}
              </span>
            </div>

            {/* Photo actions */}
            <div className="acct-hero__photo-actions">
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={photoUploading}
              >
                <Camera size={14} /> Upload Photo
              </AppButton>
              {(user.profilePhoto || photoPreview) && (
                <AppButton
                  variant="ghost"
                  size="sm"
                  onClick={handlePhotoRemove}
                  disabled={photoUploading}
                >
                  <Trash2 size={14} /> Remove
                </AppButton>
              )}
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ---- Feedback message ---- */}
      {profileMsg.text && (
        <div className={`acct-alert acct-alert--${profileMsg.type}`}>
          {profileMsg.type === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{profileMsg.text}</span>
        </div>
      )}

      <div className="acct-grid">
        {/* ---- Personal Info Card ---- */}
        <AppCard className="acct-card">
          <AppCardHeader>
            <AppCardTitle>
              <User size={18} /> Personal Information
            </AppCardTitle>
            {!editProfile && (
              <AppButton
                variant="outline"
                size="sm"
                onClick={() => {
                  setProfileMsg({ type: "", text: "" });
                  setEditProfile(true);
                }}
              >
                <Edit3 size={14} /> Edit
              </AppButton>
            )}
          </AppCardHeader>
          <AppCardContent>
            {!editProfile ? (
              /* ---- View Mode ---- */
              <div className="acct-fields">
                <div className="acct-field">
                  <span className="acct-field__label"><User size={14} /> Full Name</span>
                  <span className="acct-field__value">{user.name}</span>
                </div>
                <div className="acct-field">
                  <span className="acct-field__label"><Mail size={14} /> Email Address</span>
                  <span className="acct-field__value">{user.email}</span>
                </div>
                <div className="acct-field">
                  <span className="acct-field__label"><Phone size={14} /> Phone Number</span>
                  <span className="acct-field__value">{user.phone || "—"}</span>
                </div>
                <div className="acct-field">
                  <span className="acct-field__label"><Shield size={14} /> Role</span>
                  <span className="acct-field__value">{roleLabel(user.usertype)}</span>
                </div>
                <div className="acct-field">
                  <span className="acct-field__label"><Calendar size={14} /> Member Since</span>
                  <span className="acct-field__value">{formatDate(user.createdAt)}</span>
                </div>
              </div>
            ) : (
              /* ---- Edit Mode ---- */
              <form className="acct-form" onSubmit={handleProfileSubmit}>
                <div className="acct-form__group">
                  <label className="acct-form__label">Full Name</label>
                  <div className="acct-form__input-wrap">
                    <User size={16} className="acct-form__input-icon" />
                    <input
                      className="acct-form__input"
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="acct-form__group">
                  <label className="acct-form__label">Email Address</label>
                  <div className="acct-form__input-wrap">
                    <Mail size={16} className="acct-form__input-icon" />
                    <input className="acct-form__input acct-form__input--disabled" type="email" value={user.email} readOnly />
                  </div>
                  <span className="acct-form__hint">Email cannot be changed</span>
                </div>
                <div className="acct-form__group">
                  <label className="acct-form__label">Phone Number</label>
                  <div className="acct-form__input-wrap">
                    <Phone size={16} className="acct-form__input-icon" />
                    <input
                      className={`acct-form__input${user.usertype === "student" ? " acct-form__input--disabled" : ""}`}
                      type="text"
                      value={profile.phone}
                      onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                      maxLength={10}
                      pattern="[0-9]{10}"
                      readOnly={user.usertype === "student"}
                      required
                    />
                  </div>
                  {user.usertype === "student" && (
                    <span className="acct-form__hint">Students cannot change their phone number</span>
                  )}
                </div>
                <div className="acct-form__actions">
                  <AppButton type="submit" disabled={profileSaving}>
                    {profileSaving ? <Spinner size="sm" animation="border" /> : <Save size={14} />}
                    Save Changes
                  </AppButton>
                  <AppButton
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setProfile({ name: user.name, phone: user.phone || "" });
                      setProfileMsg({ type: "", text: "" });
                      setEditProfile(false);
                    }}
                  >
                    <X size={14} /> Cancel
                  </AppButton>
                </div>
              </form>
            )}
          </AppCardContent>
        </AppCard>

        {/* ---- Security Card ---- */}
        <AppCard className="acct-card">
          <AppCardHeader>
            <AppCardTitle>
              <Lock size={18} /> Security
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {!editPassword ? (
              <div className="acct-security-view">
                <div className="acct-security-view__row">
                  <div>
                    <h4 className="acct-security-view__title">Password</h4>
                    <p className="acct-security-view__desc">
                      Update your password to keep your account secure.
                    </p>
                  </div>
                  <AppButton
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPwMsg({ type: "", text: "" });
                      setEditPassword(true);
                    }}
                  >
                    <Edit3 size={14} /> Change
                  </AppButton>
                </div>
                {pwMsg.text && (
                  <div className={`acct-alert acct-alert--${pwMsg.type}`}>
                    {pwMsg.type === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                    <span>{pwMsg.text}</span>
                  </div>
                )}
              </div>
            ) : (
              <form className="acct-form" onSubmit={handlePasswordSubmit}>
                <div className="acct-form__group">
                  <label className="acct-form__label">Current Password</label>
                  <div className="acct-form__input-wrap">
                    <Lock size={16} className="acct-form__input-icon" />
                    <input
                      className="acct-form__input"
                      type={showOldPw ? "text" : "password"}
                      value={pw.oldPassword}
                      onChange={(e) => setPw((p) => ({ ...p, oldPassword: e.target.value }))}
                      required
                    />
                    <button type="button" className="acct-form__eye" onClick={() => setShowOldPw(!showOldPw)}>
                      {showOldPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="acct-form__group">
                  <label className="acct-form__label">New Password</label>
                  <div className="acct-form__input-wrap">
                    <Lock size={16} className="acct-form__input-icon" />
                    <input
                      className="acct-form__input"
                      type={showNewPw ? "text" : "password"}
                      value={pw.newPassword}
                      onChange={(e) => setPw((p) => ({ ...p, newPassword: e.target.value }))}
                      required
                      minLength={6}
                    />
                    <button type="button" className="acct-form__eye" onClick={() => setShowNewPw(!showNewPw)}>
                      {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="acct-form__group">
                  <label className="acct-form__label">Confirm New Password</label>
                  <div className="acct-form__input-wrap">
                    <Lock size={16} className="acct-form__input-icon" />
                    <input
                      className="acct-form__input"
                      type="password"
                      value={pw.confirmPassword}
                      onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                {pwMsg.text && (
                  <div className={`acct-alert acct-alert--${pwMsg.type}`}>
                    {pwMsg.type === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                    <span>{pwMsg.text}</span>
                  </div>
                )}
                <div className="acct-form__actions">
                  <AppButton type="submit" disabled={pwSaving}>
                    {pwSaving ? <Spinner size="sm" animation="border" /> : <Save size={14} />}
                    Update Password
                  </AppButton>
                  <AppButton
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setPw({ oldPassword: "", newPassword: "", confirmPassword: "" });
                      setPwMsg({ type: "", text: "" });
                      setEditPassword(false);
                    }}
                  >
                    <X size={14} /> Cancel
                  </AppButton>
                </div>
              </form>
            )}
          </AppCardContent>
        </AppCard>
      </div>

      {/* ---- Documents Section ---- */}
      <div className="acct-grid">
        {/* ---- Government ID Card ---- */}
        <AppCard className="acct-card">
          <AppCardHeader>
            <AppCardTitle>
              <CreditCard size={18} /> Government ID Card
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <p className="acct-doc-desc">
              Upload a government-issued photo ID (Aadhaar, PAN, Passport, Driving License, etc.) as an image or PDF.
            </p>
            <input
              type="file"
              ref={govtIdRef}
              accept="image/*,application/pdf"
              style={{ display: "none" }}
              onChange={handleGovtIdSelect}
            />
            {govtIdKey ? (
              <div className="acct-doc-preview">
                {govtIdPreview ? (
                  <img src={govtIdPreview} alt="Government ID" className="acct-doc-preview__img" />
                ) : (
                  <div className="acct-doc-preview__file">
                    <FileText size={32} />
                    <span>PDF Document</span>
                  </div>
                )}
                <div className="acct-doc-preview__actions">
                  <AppButton variant="outline" size="sm" onClick={handleGovtIdView}>
                    <Download size={14} /> View
                  </AppButton>
                  <AppButton variant="outline" size="sm" onClick={() => govtIdRef.current?.click()} disabled={govtIdUploading}>
                    <Upload size={14} /> Replace
                  </AppButton>
                  <AppButton variant="ghost" size="sm" onClick={handleGovtIdRemove} disabled={govtIdUploading}>
                    <Trash2 size={14} /> Remove
                  </AppButton>
                </div>
              </div>
            ) : (
              <div className="acct-doc-upload-zone" onClick={() => govtIdRef.current?.click()}>
                {govtIdUploading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <Upload size={24} />
                    <span>Click to upload Government ID</span>
                    <span className="acct-doc-upload-zone__hint">Image or PDF, max 10 MB</span>
                  </>
                )}
              </div>
            )}
          </AppCardContent>
        </AppCard>

        {/* ---- Student Resume (students only) ---- */}
        {user.usertype === "student" && (
          <AppCard className="acct-card">
            <AppCardHeader>
              <AppCardTitle>
                <File size={18} /> My Resume
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <p className="acct-doc-desc">
                Upload your resume in PDF or DOCX format. This may be shared with potential employers during placement.
              </p>
              <input
                type="file"
                ref={resumeRef}
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                style={{ display: "none" }}
                onChange={handleResumeSelect}
              />
              {resumeKey ? (
                <div className="acct-doc-preview">
                  <div className="acct-doc-preview__file">
                    <FileText size={32} />
                    <span>{resumeKey.endsWith(".pdf") ? "PDF Resume" : "DOCX Resume"}</span>
                  </div>
                  <div className="acct-doc-preview__actions">
                    <AppButton variant="outline" size="sm" onClick={handleResumeView}>
                      <Eye size={14} /> View
                    </AppButton>
                    <AppButton variant="outline" size="sm" onClick={() => resumeRef.current?.click()} disabled={resumeUploading}>
                      <Upload size={14} /> Replace
                    </AppButton>
                    <AppButton variant="ghost" size="sm" onClick={handleResumeRemove} disabled={resumeUploading}>
                      <Trash2 size={14} /> Remove
                    </AppButton>
                  </div>
                </div>
              ) : (
                <div className="acct-doc-upload-zone" onClick={() => resumeRef.current?.click()}>
                  {resumeUploading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <Upload size={24} />
                      <span>Click to upload your Resume</span>
                      <span className="acct-doc-upload-zone__hint">PDF or DOCX, max 10 MB</span>
                    </>
                  )}
                </div>
              )}
            </AppCardContent>
          </AppCard>
        )}
      </div>

      <FileViewerModal
        show={showResumeModal}
        onHide={() => setShowResumeModal(false)}
        fileUrl={resumeModalUrl}
        fileName={resumeKey.endsWith(".pdf") ? "Resume.pdf" : "Resume.docx"}
        fileType={resumeKey.endsWith(".pdf") ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document"}
      />
    </div>
  );
};

export default AccountSettingsPage;
